import React, { useState } from "react";
import PhoneInput from "../../../../shared/PhoneInput";
import NameInput from "../../../../shared/NameInput";
import {
  FiMinus,
  FiEye,
  FiEyeOff,
  FiPlus,
  FiEdit2,
  FiCheck,
} from "react-icons/fi";
import { 
  CARD_BASE, 
  INPUT_BASE, 
  SELECT_BASE, 
  LABEL_BASE, 
  SECTION_HEADING,
  CARD_SPACING 
} from "../../../../../constants/styles";

export default function FamilyMemberCard({
  index,
  member = {},
  errors = {},
  prefixes = [],
  suffixes = [],
  genderTypes = [],
  relationshipTypes = [],
  stateOptions = [],
  onFieldChange,
  onRemove,
  onDependentChange,
  isAddCard = false,
  onClick,
  className = "",
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [showSSN, setShowSSN] = useState(false);

  if (isAddCard) {
    return (
      <div
        onClick={onClick}
        className={`${CARD_BASE} border-dashed border-2 border-gray-300 cursor-pointer opacity-75 hover:opacity-100 ${className}`}
      >
        <div className="flex flex-col items-center justify-center h-full text-blue-600 py-4 md:py-6">
          <FiPlus size={20} />
          <span className="mt-2 text-xs md:text-sm">Add Family Member</span>
        </div>
      </div>
    );
  }

  const toggleSSN = () => onFieldChange(index, "showSSN", !member.showSSN);

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleDependentChange = (e) => {
    const isDependent = e.target.checked;
    onFieldChange(index, "dependentStatus", isDependent);
    if (onDependentChange) {
      onDependentChange(index, isDependent);
    }
  };

  return (
    <div className={`${CARD_BASE} border relative`}>
      {/* Header with edit/remove buttons */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-[#131d80] text-white flex items-center justify-center text-sm md:text-base font-semibold mr-2">
            {member.firstName ? member.firstName[0].toUpperCase() : ""}
          </div>
          <h3 className={SECTION_HEADING}>
            {member.firstName} {member.lastName}
          </h3>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <button
            type="button"
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
            className="p-1 md:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 group relative"
            title={isEditing ? "Save Changes" : "Edit Information"}
          >
            {isEditing ? (
              <FiCheck className="h-4 w-4 text-green-600 group-hover:text-green-700" />
            ) : (
              <FiEdit2 className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:text-gray-400" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 md:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 group relative"
            title="Remove Member"
          >
            <FiMinus className="h-4 w-4 text-red-400 group-hover:text-red-600" />
          </button>
        </div>
      </div>

      {isEditing ? (
        <>
          {/* Name row */}
          <div className={`flex ${CARD_SPACING.grid}`}>
            {/* Prefix */}
            <div className="w-20">
              <label className={LABEL_BASE}>Prefix</label>
              <select
                value={member.prefix}
                onChange={(e) => onFieldChange(index, "prefix", e.target.value)}
                className={SELECT_BASE}
              >
                <option value="">—</option>
                {prefixes.map((p) => (
                  <option key={p.name_prefix} value={p.name_prefix}>
                    {p.name_prefix}
                  </option>
                ))}
              </select>
            </div>

            {/* First / Middle / Last / Suffix */}
            <div className="flex-[1.5]">
              <label className={LABEL_BASE}>First Name</label>
              <NameInput
                className={INPUT_BASE}
                value={member.firstName}
                onChange={(e) =>
                  onFieldChange(index, "firstName", e.target.value)
                }
                nickname={member.preferredName}
                onNicknameChange={(nick) =>
                  onFieldChange(index, "preferredName", nick)
                }
              />
            </div>
            <div className="flex-1">
              <label className={LABEL_BASE}>Middle Name</label>
              <input
                type="text"
                className={INPUT_BASE}
                value={member.middleName}
                onChange={(e) =>
                  onFieldChange(index, "middleName", e.target.value)
                }
              />
            </div>
            <div className="flex-[1.5]">
              <label className={LABEL_BASE}>Last Name</label>
              <NameInput
                className={INPUT_BASE}
                value={member.lastName}
                onChange={(e) =>
                  onFieldChange(index, "lastName", e.target.value)
                }
              />
            </div>
            <div className="w-20">
              <label className={LABEL_BASE}>Suffix</label>
              <select
                value={member.suffix}
                onChange={(e) => onFieldChange(index, "suffix", e.target.value)}
                className={SELECT_BASE}
              >
                <option value="">—</option>
                {suffixes.map((s) => (
                  <option key={s.name_suffix} value={s.name_suffix}>
                    {s.name_suffix}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-28">
              <label className={LABEL_BASE}>Gender</label>
              <select
                value={member.gender}
                onChange={(e) => onFieldChange(index, "gender", e.target.value)}
                className={SELECT_BASE}
              >
                <option value="">—</option>
                {genderTypes.map((g) => (
                  <option key={g.gender_type_id} value={g.gender_type_id}>
                    {g.gender_type_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Details grid */}
          <div className={`grid grid-cols-5 ${CARD_SPACING.grid} mt-2`}>
            {/* DOB */}
            <div>
              <label className={LABEL_BASE}>Date of Birth</label>
              <input
                type="date"
                className={INPUT_BASE}
                value={member.dateOfBirth}
                onChange={(e) =>
                  onFieldChange(index, "dateOfBirth", e.target.value)
                }
              />
            </div>

            {/* Relationship */}
            <div>
              <label className={LABEL_BASE}>Relationship</label>
              <select
                className={SELECT_BASE}
                value={member.relationship}
                onChange={(e) =>
                  onFieldChange(index, "relationship", e.target.value)
                }
              >
                <option value="">—</option>
                {relationshipTypes.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Email */}
            <div>
              <label className={LABEL_BASE}>Email</label>
              <input
                type="email"
                className={`${INPUT_BASE} ${errors.email ? "border-red-500" : ""}`}
                value={member.email}
                onChange={(e) => onFieldChange(index, "email", e.target.value)}
              />
            </div>

            {/* Phone */}
            <div>
              <label className={LABEL_BASE}>Phone</label>
              <PhoneInput
                className={INPUT_BASE}
                value={member.phoneNumber}
                onChange={(val) => onFieldChange(index, "phoneNumber", val)}
                type={member.phoneType}
                onTypeChange={(t) => onFieldChange(index, "phoneType", t)}
              />
            </div>

            {/* SSN */}
            <div>
              <label className={LABEL_BASE}>SSN</label>
              <div className="relative">
                <input
                  type={member.showSSN ? "text" : "password"}
                  className={`${INPUT_BASE} ${errors.ssn ? "border-red-500" : ""}`}
                  value={member.ssn}
                  onChange={(e) => onFieldChange(index, "ssn", e.target.value)}
                />
                <button
                  type="button"
                  onClick={toggleSSN}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {member.showSSN ? (
                    <FiEyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <FiEye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Flags */}
          <div className={`flex ${CARD_SPACING.grid} mt-2`}>
            <label
              key="dependentStatus"
              className="inline-flex items-center text-xs md:text-sm"
            >
              <input
                type="checkbox"
                checked={member.dependentStatus}
                onChange={handleDependentChange}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2">Dependent</span>
            </label>
            {["isTrustedContact", "isHouseholdMember"].map((flag) => (
              <label key={flag} className="inline-flex items-center text-xs md:text-sm">
                <input
                  type="checkbox"
                  checked={member[flag]}
                  onChange={(e) => onFieldChange(index, flag, e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <span className="ml-2">
                  {
                    {
                      dependentStatus: "Dependent",
                      isTrustedContact: "Trusted Contact",
                      isHouseholdMember: "Household Member",
                    }[flag]
                  }
                </span>
              </label>
            ))}
          </div>

          {/* Address (if not household) */}
          {!member.isHouseholdMember && (
            <div className={`grid grid-cols-12 ${CARD_SPACING.grid} mt-2`}>
              {[
                ["street", "Street", 5],
                ["unit", "Unit/Apt", 1],
                ["city", "City", 2],
                ["state", "State", 2],
                ["zipCode", "ZIP", 2],
              ].map(([field, label, span]) => (
                <div key={field} className={`col-span-${span}`}>
                  <label className={LABEL_BASE}>{label}</label>
                  {field === "state" ? (
                    <select
                      className={SELECT_BASE}
                      value={member.address[field]}
                      onChange={(e) =>
                        onFieldChange(index, `address.${field}`, e.target.value)
                      }
                    >
                      <option value="">—</option>
                      {stateOptions.map((s) => (
                        <option key={s.state_id} value={s.state_code}>
                          {s.state_code}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className={INPUT_BASE}
                      value={member.address[field]}
                      onChange={(e) =>
                        onFieldChange(index, `address.${field}`, e.target.value)
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Read-only view */}
          <div className={CARD_SPACING.section}>
            <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  Gender
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">
                  {genderTypes.find((g) => g.gender_type_id === member.gender)
                    ?.gender_type_name || "—"}
                </p>
              </div>
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  Relationship
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">
                  {relationshipTypes.find(
                    (r) => r.value === member.relationship,
                  )?.label || "—"}
                </p>
              </div>
            </div>

            <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  Date of Birth
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">{member.dateOfBirth || "—"}</p>
              </div>
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  Email
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">{member.email || "—"}</p>
              </div>
            </div>

            <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  Phone
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">{member.phoneNumber || "—"}</p>
              </div>
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  SSN
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">••••••••••</p>
              </div>
            </div>

            <div className="flex gap-2">
              {member.dependentStatus && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Dependent
                </span>
              )}
              {member.isTrustedContact && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Trusted Contact
                </span>
              )}
              {!member.isHouseholdMember && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:text-gray-200">
                  External Member
                </span>
              )}
            </div>

            {!member.isHouseholdMember && (
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-500 dark:text-gray-500">
                  Address
                </label>
                <p className="text-xs md:text-sm text-gray-900 dark:text-gray-100">
                  {member.address.street}
                  {member.address.unit && `, ${member.address.unit}`}
                  <br />
                  {member.address.city}, {member.address.state}{" "}
                  {member.address.zipCode}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
